import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface FieldPayloadItem {
  isFocused: boolean;
  isFilled: boolean;
}

interface ContextPayload {
  isFormReady: boolean;
  fieldLoaded: (name: string) => void;
  setPayloadItem: (name: string, payload: FieldPayloadItem) => void;
  getPayloadItem: (name: string) => FieldPayloadItem;
}

const defaultValueContextPayload: ContextPayload = {
  isFormReady: true,
  fieldLoaded: (_: string) => {
    throw new Error(
      'fieldLoaded function must be used within a CashierFieldStateProvider',
    );
  },
  setPayloadItem: (_, __) => {
    throw new Error(
      'setPayloadItem function must be used within a CashierFieldStateProvider',
    );
  },
  getPayloadItem: (_: string) => {
    throw new Error(
      'getPayloadItem function must be used within a CashierFieldStateProvider',
    );
  },
};

const SecuredFormStateContext = createContext<ContextPayload>(
  defaultValueContextPayload,
);

interface ProviderProps {
  initialPayload: Record<string, FieldPayloadItem>;
  children: ReactNode;
}

export const SecuredFormStateProvider = ({
  children,
  initialPayload,
}: ProviderProps) => {
  const [fieldPayloadMap, setFieldPayloadMap] = useState<
    Record<
      string,
      {
        isReady: boolean;
        isFocused: boolean;
        isFilled: boolean;
      }
    >
  >(
    Object.fromEntries(
      Object.entries(initialPayload).map(([name, payloadItem]) => [
        name,
        {
          ...payloadItem,
          isReady: false,
        },
      ]),
    ) as Record<
      string,
      {
        isReady: boolean;
        isFocused: boolean;
        isFilled: boolean;
      }
    >,
  );

  const isFormReady = useMemo(
    () => Object.values(fieldPayloadMap).every(({ isReady }) => isReady),
    [fieldPayloadMap],
  );

  const fieldLoaded = useCallback(
    (name: string) => {
      if (fieldPayloadMap[name] === undefined) {
        return;
      }

      setFieldPayloadMap({
        ...fieldPayloadMap,
        [name]: {
          ...fieldPayloadMap[name],
          isReady: true,
        },
      });
    },
    [fieldPayloadMap],
  );

  const setPayloadItem = useCallback(
    (name: string, payload: FieldPayloadItem) => {
      setFieldPayloadMap({
        ...fieldPayloadMap,
        [name]: {
          ...fieldPayloadMap[name],
          ...payload,
        },
      });
    },
    [fieldPayloadMap],
  );

  const getPayloadItem = useCallback(
    (name: string): FieldPayloadItem => {
      const payloadItem = fieldPayloadMap[name];

      if (!payloadItem) {
        return {
          isFilled: false,
          isFocused: false,
        };
      }

      return {
        isFilled: payloadItem.isFilled,
        isFocused: payloadItem.isFocused,
      };
    },
    [fieldPayloadMap],
  );

  const contextValue: ContextPayload = useMemo(() => {
    return {
      fieldLoaded,
      isFormReady,
      setPayloadItem,
      getPayloadItem,
    };
  }, [fieldLoaded, getPayloadItem, isFormReady, setPayloadItem]);

  return (
    <SecuredFormStateContext.Provider value={contextValue}>
      {children}
    </SecuredFormStateContext.Provider>
  );
};

export const useSecuredFormStateContext = () =>
  useContext(SecuredFormStateContext);
